import type { CopyModuleSortBy, CopyModuleStep, CopyModuleTab, CopySourceCourse, ModuleItemType } from 'app/types/modules';
import type { Tab } from 'core/modules/components/tabs/HeaderTabs/useHeaderTabs';
import { useTranslate } from 'core/hooks/useTranslate';
import { stripHtml } from 'core/utils/utils';
import { ROLES_GUID } from 'core/utils/user';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import type { SelectCustomizableOption } from '../../../../../atoms/SelectCustomizable/SelectCustomizable';
import { dialogDescriptionId } from '../../../../../organisms/dialogs/Dialog/useDialog';
import useCopySourceCourses from './useCopySourceCourses';
import useCopySourceModules from './useCopySourceModules';

/**
 * Raíz del `TrapFocus` del `Dialog` (MUI `Modal` la usa como `rootRef`). Es el
 * destino del portal del menú del selector "Ordenar por": dentro del trap, o el
 * trap le roba el foco y el desplegable queda inalcanzable por teclado.
 */
const DIALOG_TRAP_ROOT_SELECTOR = '.MuiDialog-container';

const DIALOG_ID = 'copy-module-dialog';

/** `name` compartido por cada lista de radios (exclusión mutua + flechas). */
export const COURSE_RADIO_NAME = 'copy-module-course';
export const MODULE_RADIO_NAME = 'copy-module-module';

const TAB_GROUPS: CopyModuleTab = 'groups';
const TAB_PROGRAMS: CopyModuleTab = 'programs';

const SORT_BY_SUBJECT: CopyModuleSortBy = 'subject';
const SORT_BY_LEVEL: CopyModuleSortBy = 'level';

/** Sección de la lista del paso 1: un grupo-clase, una materia o un nivel. */
export type CopyModuleCourseGroup = {
  courses: CopySourceCourse[];
  id: string;
  title: string;
};

/** Grupo en construcción: conserva las dos claves de orden del nivel educativo. */
type CopyModuleCourseGroupDraft = CopyModuleCourseGroup & {
  levelGuid: string | null;
  order: number | null;
};

/** Vista de un módulo en la lista del paso 2 (autoría ya resuelta). */
export type CopyModuleOption = {
  authorAvatar: string;
  authorName: string;
  authorRole: string;
  guid: string;
  name: string;
  shouldShowAuthor: boolean;
};

type UseCopyModuleDialogParams = {
  isOpen: boolean;
  onClose: () => void;
  onConfirmCopy: (sourceModule: ModuleItemType) => Promise<void>;
  programGuid: string;
};

type UseCopyModuleDialogReturn = {
  activeTab: CopyModuleTab;
  dialogId: string;
  courseGroups: CopyModuleCourseGroup[];
  handleChangeSortBy: (option: unknown) => void;
  handleChangeTab: (tabId: string) => void;
  handleClose: () => void;
  handlePrimaryAction: () => void;
  handleSelectCourse: (courseGuid: string) => void;
  handleSelectModule: (moduleGuid: string) => void;
  hasCoursesError: boolean;
  hasModulesError: boolean;
  isCopying: boolean;
  isLoadingCourses: boolean;
  isLoadingModules: boolean;
  isPrimaryActionDisabled: boolean;
  isStepSelectCourse: boolean;
  menuPortalTarget: HTMLElement | null;
  moduleOptions: CopyModuleOption[];
  primaryButtonText: string;
  selectedCourse: CopySourceCourse | null;
  selectedCourseGuid: string | null;
  selectedModuleGuid: string | null;
  shouldShowCourseGroup: boolean;
  shouldShowCourses: boolean;
  shouldShowCoursesEmpty: boolean;
  shouldShowModules: boolean;
  shouldShowModulesEmpty: boolean;
  shouldShowSortBy: boolean;
  sortByLabel: string;
  sortByOptions: SelectCustomizableOption[];
  sortByValue: SelectCustomizableOption | null;
  stepDescriptionId: string;
  stepRef: React.RefObject<HTMLDivElement>;
  stepSubtitle: string;
  t: (key: string, options?: Record<string, unknown>) => string;
  tablistLabel: string;
  tabs: Tab[];
  title: string;
};

/**
 * Lógica del wizard "Copiar módulo" (TAN-10190): paso, pestaña, orden y
 * selección, más los datos derivados. El componente es JSX puro.
 *
 * La copia la ejecuta el diálogo padre vía `onConfirmCopy` (TAN-10191), que es
 * quien conoce el periodo destino e inserta el módulo en la estructura; aquí
 * queda el feedback de la operación (loading + toasts de éxito/error). Los
 * cursos y módulos de origen los sirven los CRUD reales (TAN-10192): el paso 1
 * desde `GET v3/front/teacher-courses`, el paso 2 desde los items del curso.
 */
function useCopyModuleDialog({ isOpen, onClose, onConfirmCopy, programGuid }: UseCopyModuleDialogParams): UseCopyModuleDialogReturn {
  const { t } = useTranslate();
  const { addToast } = useToasts();

  const [step, setStep] = useState<CopyModuleStep>('select-course');
  const [isCopying, setIsCopying] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<CopyModuleTab>(TAB_GROUPS);
  const [sortBy, setSortBy] = useState<CopyModuleSortBy>(SORT_BY_SUBJECT);
  const [selectedCourseGuid, setSelectedCourseGuid] = useState<string | null>(null);
  const [selectedModuleGuid, setSelectedModuleGuid] = useState<string | null>(null);
  const [menuPortalTarget, setMenuPortalTarget] = useState<HTMLElement | null>(null);

  const isStepSelectCourse = step === 'select-course';

  const stepRef = useRef<HTMLDivElement>(null);
  const isFirstFocusEffect = useRef<boolean>(true);

  // El cambio de paso reemplaza el contenido del diálogo: se lleva el foco
  // al contenedor del paso (rol `group` con nombre accesible) para que el lector
  // anuncie dónde está. En el primer render no, que el foco lo gobierna el
  // `Dialog` al abrirse.
  useEffect(() => {
    if (isFirstFocusEffect.current) {
      isFirstFocusEffect.current = false;
      return;
    }
    stepRef.current?.focus();
  }, [step]);

  // El contenido del diálogo solo existe en el DOM cuando está abierto, así que
  // la raíz del trap se resuelve tras montar (y por paso, porque el contenedor
  // se reemplaza).
  useEffect(() => {
    setMenuPortalTarget(stepRef.current?.closest<HTMLElement>(DIALOG_TRAP_ROOT_SELECTOR) ?? null);
  }, [isOpen, step]);

  // El backend excluye los cursos del programa actual vía `exclude_program`:
  // la lista que llega ya es la de orígenes válidos.
  const { courses: availableCourses, hasError: hasCoursesError, isLoading: isLoadingCourses } = useCopySourceCourses({ isOpen, programGuid });

  const ungroupedLabel = t('organize-content:Ungrouped courses');

  // «Grupos» agrupa por clase, así que solo lista los cursos que cuelgan de una
  // (`referenced`). Los programas licenciados no tienen grupo-clase y llenarían
  // la pestaña con un cajón «Sin grupo» que duplica «Programas» — con los datos
  // reales de un profesor son la mayoría de la lista (TAN-10192).
  const tabCourses = useMemo(
    () => (activeTab === TAB_GROUPS ? availableCourses.filter((course) => Boolean(course?.school_group_guid)) : availableCourses),
    [activeTab, availableCourses]
  );

  // La pestaña «Programas» ordenada por nivel educativo no puede ordenarse
  // alfabéticamente ("10º" no va entre "1º" y "2º").
  const isSortedByLevel = activeTab === TAB_PROGRAMS && sortBy === SORT_BY_LEVEL;

  const courseGroups = useMemo<CopyModuleCourseGroup[]>(() => {
    const groups = new Map<string, CopyModuleCourseGroupDraft>();

    tabCourses.forEach((course) => {
      const key = getGroupKey(course, activeTab, sortBy);
      const group = groups.get(key);
      if (group) {
        group.courses.push(course);
        return;
      }
      groups.set(key, {
        courses: [course],
        id: `${activeTab}-${key}`,
        levelGuid: course?.education_level_guid ?? null,
        order: course?.education_year_order ?? null,
        title: getGroupTitle(course, activeTab, sortBy, ungroupedLabel),
      });
    });

    return [...groups.values()]
      .sort((groupA, groupB) => (isSortedByLevel ? compareGroupsByLevel(groupA, groupB) : groupA.title.localeCompare(groupB.title)))
      .map(({ courses, id, title }) => ({ courses, id, title }));
  }, [activeTab, isSortedByLevel, sortBy, tabCourses, ungroupedLabel]);

  const selectedCourse = useMemo(() => availableCourses.find((course) => course.guid === selectedCourseGuid) ?? null, [availableCourses, selectedCourseGuid]);

  const { hasError: hasModulesError, isLoading: isLoadingModules, modules: sourceModules } = useCopySourceModules({ course: selectedCourse });

  const moduleOptions = useMemo(() => sourceModules.map(toModuleOption), [sourceModules]);

  const tabs: Tab[] = [
    { id: TAB_GROUPS, name: t('organize-content:Groups') },
    { id: TAB_PROGRAMS, name: t('organize-content:Programs') },
  ];

  const sortByOptions: SelectCustomizableOption[] = [
    { label: t('common:Subject'), value: SORT_BY_SUBJECT },
    { label: t('common:Education level'), value: SORT_BY_LEVEL },
  ];

  const sortByValue = sortByOptions.find((option) => option.value === sortBy) ?? null;

  const title = t('organize-content:Copy module');
  const stepSubtitle = isStepSelectCourse ? t('organize-content:Copy module step select course') : t('organize-content:Copy module step select module');
  const primaryButtonText = isStepSelectCourse ? t('actions:Next') : title;
  // Sin datos (cargando o con error) no hay nada que elegir: el paso no avanza.
  const isStepBlocked = isStepSelectCourse ? isLoadingCourses || hasCoursesError : isLoadingModules || hasModulesError;
  const isPrimaryActionDisabled = isCopying || isStepBlocked || (isStepSelectCourse ? !selectedCourseGuid : !selectedModuleGuid);

  const hasCourses = courseGroups.length > 0;
  const hasModules = moduleOptions.length > 0;
  const isCoursesListResolved = !isLoadingCourses && !hasCoursesError;
  const isModulesListResolved = !isLoadingModules && !hasModulesError;

  function resetState(): void {
    setStep('select-course');
    setActiveTab(TAB_GROUPS);
    setSortBy(SORT_BY_SUBJECT);
    setSelectedCourseGuid(null);
    setSelectedModuleGuid(null);
  }

  function handleClose(): void {
    // Cerrar a media copia dejaría el toast sobre un diálogo que ya no está.
    if (isCopying) return;

    resetState();
    onClose();
  }

  async function handleConfirmCopy(): Promise<void> {
    const selectedModule = sourceModules.find((module) => module.guid === selectedModuleGuid);
    if (!selectedModule) return;

    setIsCopying(true);
    try {
      await onConfirmCopy(selectedModule);

      // El orden importa: `handleClose()` desmonta este wizard (el padre lo monta
      // con `copyModuleState.isOpen &&`), y en React 17 los `setState` posteriores
      // a un `await` no se agrupan, así que dejar la bajada del flag después del
      // cierre avisa de un update sobre un componente ya desmontado.
      setIsCopying(false);
      addToast(t('organize-content:Copy module ok'), {
        appearance: 'success',
        autoDismiss: true,
      });
      handleClose();
    } catch {
      setIsCopying(false);
      addToast(t('organize-content:Copy module error'), {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  }

  function handlePrimaryAction(): void {
    if (isStepSelectCourse) {
      // Cambiar de curso invalida cualquier módulo elegido antes.
      setSelectedModuleGuid(null);
      setStep('select-module');
      return;
    }
    void handleConfirmCopy();
  }

  function handleChangeTab(tabId: string): void {
    if (tabId !== TAB_GROUPS && tabId !== TAB_PROGRAMS) return;

    // «Grupos» no lista los programas sin clase: mantener uno elegido en
    // «Programas» dejaría el botón activo con una selección que no se ve.
    if (tabId === TAB_GROUPS && selectedCourse && !selectedCourse.school_group_guid) {
      setSelectedCourseGuid(null);
    }

    setActiveTab(tabId);
  }

  function handleChangeSortBy(option: unknown): void {
    const nextSortBy = (option as SelectCustomizableOption | null)?.value;
    if (nextSortBy !== SORT_BY_SUBJECT && nextSortBy !== SORT_BY_LEVEL) return;
    setSortBy(nextSortBy);
  }

  function handleSelectCourse(courseGuid: string): void {
    setSelectedCourseGuid(courseGuid);
  }

  function handleSelectModule(moduleGuid: string): void {
    setSelectedModuleGuid(moduleGuid);
  }

  return {
    activeTab,
    courseGroups,
    dialogId: DIALOG_ID,
    handleChangeSortBy,
    handleChangeTab,
    handleClose,
    handlePrimaryAction,
    handleSelectCourse,
    handleSelectModule,
    hasCoursesError,
    hasModulesError,
    isCopying,
    isLoadingCourses,
    isLoadingModules,
    isPrimaryActionDisabled,
    isStepSelectCourse,
    menuPortalTarget,
    moduleOptions,
    primaryButtonText,
    selectedCourse,
    selectedCourseGuid,
    selectedModuleGuid,
    // En «Grupos» lo dice la `<legend>`; en «Programas» sin él 4ºA y 4ºB son indistinguibles.
    shouldShowCourseGroup: activeTab === TAB_PROGRAMS,
    shouldShowCourses: isCoursesListResolved && hasCourses,
    shouldShowCoursesEmpty: isCoursesListResolved && !hasCourses,
    shouldShowModules: isModulesListResolved && hasModules,
    shouldShowModulesEmpty: isModulesListResolved && !hasModules,
    shouldShowSortBy: activeTab === TAB_PROGRAMS,
    sortByLabel: t('common:Sort by'),
    sortByOptions,
    sortByValue,
    // El paso lo reutiliza como nombre: repetir la cadena la anunciaría dos veces.
    stepDescriptionId: dialogDescriptionId(DIALOG_ID),
    stepRef,
    stepSubtitle,
    t,
    tablistLabel: t('organize-content:Copy module course tabs'),
    tabs,
    title,
  };
}

/**
 * Orden de los grupos de la vista por nivel educativo: por la clave numérica del
 * nivel y, sin ella (nivel incompleto o "sin agrupar"), al final y por título.
 */
/** Nulos al final: un grupo sin la clave no debe colarse antes que los que la tienen. */
function compareNullable<T extends number | string>(valueA: T | null, valueB: T | null, compare: (a: T, b: T) => number): number | null {
  if (valueA === null && valueB === null) return null;
  if (valueA === null) return 1;
  if (valueB === null) return -1;
  const result = compare(valueA, valueB);
  return result === 0 ? null : result;
}

/**
 * Orden por nivel educativo: primero la etapa y dentro de ella el curso.
 *
 * `education_year_order` es el orden del curso DENTRO de su etapa, no uno
 * global: Infantil «3 años», Primaria «1.º» y ESO «1.º» valen todos 1. Ordenar
 * solo por él intercala las etapas (verificado contra dev1). La API no expone
 * un orden de etapa, así que se ordena por `education_level_guid`, que en el
 * catálogo es ordinal (`el-es-01` Infantil < `el-es-02` Primaria < `el-es-03`
 * ESO). Si backend llegara a exponer un `education_level_order`, sustituye a
 * este criterio.
 */
function compareGroupsByLevel(groupA: CopyModuleCourseGroupDraft, groupB: CopyModuleCourseGroupDraft): number {
  const byLevel = compareNullable(groupA.levelGuid, groupB.levelGuid, (a, b) => a.localeCompare(b));
  if (byLevel !== null) return byLevel;

  const byYear = compareNullable(groupA.order, groupB.order, (a, b) => a - b);
  if (byYear !== null) return byYear;

  return groupA.title.localeCompare(groupB.title);
}

/** Cursos sin la clave de la agrupación activa: caen todos en una única sección. */
const UNGROUPED_KEY = '__ungrouped__';

/**
 * Clave de la sección: SIEMPRE el guid, nunca el nombre.
 *
 * El nombre ni identifica ni es único. `school_group_name` es nullable y se
 * repite entre colegios (dos clases «1ºA» distintas se fundirían en una), y el
 * catálogo de disciplinas trae homónimos con guid propio — en dev1 conviven
 * «Educación artística» y «Educación Artística» como dos filas. Agrupar por
 * nombre los colapsaría; se agrupa por guid y el nombre solo se pinta.
 */
function getGroupKey(course: CopySourceCourse, activeTab: CopyModuleTab, sortBy: CopyModuleSortBy): string {
  if (activeTab === TAB_GROUPS) return course?.school_group_guid ?? UNGROUPED_KEY;
  // `education_year_guid` identifica curso Y etapa ("1.º" de Primaria y de ESO
  // son guids distintos), así que sirve de clave del agrupado por nivel.
  const guid = sortBy === SORT_BY_SUBJECT ? course?.education_discipline_guid : course?.education_year_guid;
  return guid ?? UNGROUPED_KEY;
}

function getGroupTitle(course: CopySourceCourse, activeTab: CopyModuleTab, sortBy: CopyModuleSortBy, ungroupedLabel: string): string {
  // El título es la `<legend>` del `RadioGroup`: sin reserva queda un `fieldset` sin nombre.
  if (activeTab === TAB_GROUPS) return course.school_group_name || ungroupedLabel;
  return (sortBy === SORT_BY_SUBJECT ? course.subject_name : course.educational_level_name) || ungroupedLabel;
}

function toModuleOption(module: ModuleItemType): CopyModuleOption {
  const createdBy = module.created_by;
  const authorName = [createdBy?.name, createdBy?.lastname].filter(Boolean).join(' ');

  return {
    authorAvatar: createdBy?.avatar ?? '',
    authorName,
    authorRole: createdBy?.role_guid ?? '',
    guid: module.guid,
    name: stripHtml(module.lesson_name || module.section || ''),
    // Mismo criterio que los overlays de módulo: solo se muestra la autoría de
    // lo creado/editado por profesorado. Sin nombre no se pinta: el backend real
    // puede no traer `created_by` y quedaría un avatar vacío.
    shouldShowAuthor: Boolean(authorName) && createdBy?.role_guid !== ROLES_GUID.EDITORIAL,
  };
}

export default useCopyModuleDialog;
